// /src/components/core/ShareLinkModal.jsx
// Modal for generating and sharing smart links to groups/communities
// Includes QR code generation, copy button, and optional expiry/max uses settings

import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { tokens } from '../../styles/designTokens';
import { useTranslation } from '../../i18n/useTranslation';
import Modal from './Modal';
import { isSignedIn } from '../../contexts/AuthContext';
import { smartLinkService, userActionService } from '../../data/dataService';
import { SHARE_LINK_TEXT_MAX } from '../../utils/validationConstants';

const ShareLinkModal = ({
  visible = false,
  targetType = 'community', // 'community', 'group', 'conversation', 'event', 'user'
  targetId = null,
  targetName = '',
  onClose = null,
  currentUser = null,
  context = null, // 'locality' or 'groups' - only relevant for articles/events
  isPublic = true, // Whether content is public (for articles/events)
  communityId = null // For articles/events in private groups
}) => {
  const { t, formattingLocale } = useTranslation('sharing');

  // Link generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [existingLinks, setExistingLinks] = useState([]);
  const [selectedLink, setSelectedLink] = useState(null);

  // New link form state
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [customSlug, setCustomSlug] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [expiryDays, setExpiryDays] = useState('');

  // Copy feedback
  const [copied, setCopied] = useState(false);
  const [inlineError, setInlineError] = useState('');

  // Theme based on target type and context
  // Groups/Communities always use mauve
  // Articles/Events use context-based color (blue for locality, mauve for groups)
  // Colours sourced from the single design-token layer (no hardcoded hex drift).
  const isGroupOrCommunity = targetType === 'group' || targetType === 'community';
  const isContextualContent = targetType === 'conversation' || targetType === 'event';
  const primaryColor = isGroupOrCommunity ? tokens.colors.contextGroups :
                       (isContextualContent && context === 'groups') ? tokens.colors.contextGroups :
                       tokens.colors.contextLocality;

  // Determine if content is private (not publicly shareable)
  // Private content (is_public=false) should not be shared via public social media links
  const isPrivateContent = !isPublic;

  // For public content (articles/events with is_public=true), no login required
  // For groups/communities, login is required (they control membership)
  const requiresLogin = isGroupOrCommunity;

  // Signed in means a real user id — the fresh-load guest object is truthy with id: null.
  const signedIn = isSignedIn(currentUser);

  // Debug: Log visibility changes
  useEffect(() => {
    console.log('📱 ShareLinkModal visibility changed:', {
      visible,
      targetType,
      targetId,
      targetName,
      isPublic,
      communityId,
      signedIn
    });
  }, [visible]);

  // Load existing links when modal opens
  useEffect(() => {
    if (visible && targetId) {
      loadExistingLinks();
    }
  }, [visible, targetId]);

  const loadExistingLinks = async () => {
    const links = await smartLinkService.getSmartLinksForTarget(targetType, targetId);
    setExistingLinks(links);

    // Auto-select the first active link
    if (links.length > 0) {
      setSelectedLink(links[0]);
    } else {
      // Pre-populate label suggestion if no existing links
      setLinkLabel(smartLinkService.generateLabelSuggestion(targetName));
    }
  };

  const handleGenerateLink = async () => {
    if (!targetId) {
      setInlineError(t('error_missing_target'));
      return;
    }

    // Check if private content is being shared publicly
    if (isPrivateContent) {
      setInlineError(t('error_private_content'));
      return;
    }

    // Public content can be shared without login, but we recommend being logged in
    if (!signedIn && requiresLogin) {
      setInlineError(t('error_login_required'));
      return;
    }

    // Validate label
    if (!linkLabel.trim()) {
      setInlineError(t('error_label_required'));
      return;
    }
    setInlineError('');

    setIsGenerating(true);

    try {
      // Generate slug (use custom or auto-generate) - for backward compatibility
      const slug = customSlug.trim()
        ? customSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '')
        : null; // Will be auto-set to short_code in service

      // Calculate expiry date if specified
      let expiresAt = null;
      if (expiryDays && parseInt(expiryDays) > 0) {
        const expiry = new Date();
        expiry.setDate(expiry.getDate() + parseInt(expiryDays));
        expiresAt = expiry.toISOString();
      }

      // Parse max uses
      const maxUsesValue = maxUses && parseInt(maxUses) > 0 ? parseInt(maxUses) : null;

      // Create the link
      const result = await smartLinkService.createSmartLink({
        slug,
        targetType,
        targetId,
        linkType: 'general',
        maxUses: maxUsesValue,
        expiresAt,
        label: linkLabel.trim(),
        metadata: {
          targetName,
          createdVia: 'ShareLinkModal'
        }
      });

      if (result.success) {
        console.log('✅ Smart link created with short code');
        // Reload existing links
        await loadExistingLinks();
        // Reset form
        setCustomSlug('');
        setLinkLabel(smartLinkService.generateLabelSuggestion(targetName));
        setMaxUses('');
        setExpiryDays('');
        setShowAdvancedOptions(false);
      } else {
        setInlineError(t('error_create_failed', { error: result.error }));
      }
    } catch (error) {
      console.error('Error generating smart link:', error);
      setInlineError(t('error_generate_failed'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!selectedLink) return;

    // Prefer short_code over slug
    const code = selectedLink.short_code || selectedLink.slug;
    const url = smartLinkService.getSmartLinkUrl(code);
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    // Track share engagement
    if (signedIn && targetId) {
      if (targetType === 'conversation' || targetType === 'event') {
        // Articles and events use article_engagement
        await userActionService.trackShare(targetId, currentUser.id).catch(() => {});
      } else if (targetType === 'chat' || targetType === 'comment') {
        // Chats and comments use message_engagement
        await userActionService.trackMessageEngagement(currentUser.id, targetId, 'share').catch(() => {});
      }
    }
  };

  const handleNativeShare = async () => {
    if (!selectedLink) return;

    // Prefer short_code over slug
    const code = selectedLink.short_code || selectedLink.slug;
    const url = smartLinkService.getSmartLinkUrl(code);

    // Use native share if available (mobile browsers)
    if (navigator.share) {
      try {
        await navigator.share({
          title: targetName,
          text: `${targetName} — via OurNet`,
          url: url,
        });

        // Track share engagement (only if share succeeded, not cancelled)
        if (signedIn && targetId) {
          if (targetType === 'conversation' || targetType === 'event') {
            // Articles and events use article_engagement
            await userActionService.trackShare(targetId, currentUser.id).catch(() => {});
          } else if (targetType === 'chat' || targetType === 'comment') {
            // Chats and comments use message_engagement
            await userActionService.trackMessageEngagement(currentUser.id, targetId, 'share').catch(() => {});
          }
        }
      } catch (err) {
        // User cancelled or share failed — fallback to copy
        if (err.name !== 'AbortError') {
          console.log('Share failed, falling back to copy');
          handleCopyLink();
        }
      }
    } else {
      // Desktop fallback — copy to clipboard
      handleCopyLink();
    }
  };

  const handleDeactivateLink = async (linkId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(t('confirm_deactivate'))) {
      return;
    }

    const success = await smartLinkService.deactivateSmartLink(linkId);
    if (success) {
      console.log('✅ Link deactivated');
      await loadExistingLinks();
    } else {
      setInlineError(t('error_deactivate_failed'));
    }
  };

  // Prefer short_code over slug
  const code = selectedLink ? (selectedLink.short_code || selectedLink.slug) : '';
  const fullUrl = code ? smartLinkService.getSmartLinkUrl(code) : '';

  return (
    <Modal
      isOpen={visible}
      onClose={onClose}
      panelStyle={{
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        width: '100%',
        maxWidth: '520px',
        maxHeight: '90vh',
        overflow: 'auto',
        margin: '20px'
      }}
    >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1a1a1a',
              margin: 0
            }}>
              {t('modal_title', {
                type: targetType === 'community' ? t('type_community') :
                      targetType === 'conversation' ? t('type_article') :
                      targetType === 'event' ? t('type_event') :
                      targetType === 'user' ? t('type_profile') :
                      t('type_group')
              })}
            </h2>
            <button
              onClick={onClose}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#f3f4f6',
                color: '#1a1a1a',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}
            >
              ×
            </button>
          </div>
          <p style={{
            fontSize: '14px',
            color: '#666',
            marginTop: '8px',
            marginBottom: 0
          }}>
            {targetName}
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Existing Links */}
          {existingLinks.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1a1a1a',
                marginBottom: '12px'
              }}>
                {t('your_links_title')}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {existingLinks.map(link => (
                  <div
                    key={link.id}
                    onClick={() => setSelectedLink(link)}
                    style={{
                      padding: '12px',
                      border: `2px solid ${selectedLink?.id === link.id ? primaryColor : '#e5e7eb'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s',
                      backgroundColor: selectedLink?.id === link.id ? `${primaryColor}11` : 'white'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1, marginRight: '8px' }}>
                        {link.label && (
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', marginBottom: '4px' }}>
                            {link.label}
                          </div>
                        )}
                        <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#666' }}>
                          {window.location.host}/l/{link.short_code || link.slug}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                          {t('used_count', { count: link.use_count })}
                          {link.max_uses && ` (${t('max_uses_label', { max: link.max_uses })})`}
                          {link.expires_at && ` • ${t('expires_label', { date: new Date(link.expires_at).toLocaleDateString(formattingLocale) })}`}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeactivateLink(link.id);
                        }}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          color: '#ef4444',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected Link Display */}
          {selectedLink && (
            <div style={{
              marginBottom: '24px',
              padding: '20px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              border: `2px solid ${primaryColor}33`
            }}>
              {/* QR Code */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '8px'
              }}>
                <QRCodeSVG
                  value={fullUrl}
                  size={180}
                  level="H"
                  includeMargin={true}
                  fgColor={primaryColor}
                />
              </div>

              {/* URL Display */}
              <div style={{
                padding: '12px',
                backgroundColor: 'white',
                borderRadius: '8px',
                marginBottom: '12px',
                wordBreak: 'break-all',
                fontSize: '14px',
                color: '#1a1a1a',
                fontFamily: 'monospace'
              }}>
                {fullUrl}
              </div>

              {/* Share Buttons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {/* Native Share Button (primary on mobile) */}
                {navigator.share && (
                  <button
                    onClick={handleNativeShare}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: primaryColor,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.opacity = '0.9')}
                    onMouseOut={(e) => (e.currentTarget.style.opacity = '1')}
                  >
                    📤 {t('share_button')}
                  </button>
                )}

                {/* Copy Button */}
                <button
                  onClick={handleCopyLink}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: copied ? '#10b981' : (navigator.share ? 'white' : primaryColor),
                    color: copied ? 'white' : (navigator.share ? primaryColor : 'white'),
                    border: navigator.share ? `2px solid ${primaryColor}` : 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseOver={(e) => !copied && (e.currentTarget.style.opacity = '0.9')}
                  onMouseOut={(e) => !copied && (e.currentTarget.style.opacity = '1')}
                >
                  {copied ? t('copied_button') : t('copy_button')}
                </button>
              </div>
            </div>
          )}

          {/* Create New Link */}
          <div style={{
            padding: '16px',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            border: '1px dashed #d1d5db'
          }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1a1a1a',
              marginBottom: '12px'
            }}>
              {t('create_new_link_title')}
            </h3>

            {/* Label Input (always visible) */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '4px' }}>
                {t('link_label_field')}
              </label>
              <input
                type="text"
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
                placeholder={t('link_label_placeholder')}
                maxLength={SHARE_LINK_TEXT_MAX}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                {t('character_count', { current: linkLabel.length, max: 100 })}
              </div>
            </div>

            {/* Advanced Options Toggle */}
            <button
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              style={{
                padding: '8px 12px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#666',
                cursor: 'pointer',
                marginBottom: '12px',
                width: '100%',
                textAlign: 'left'
              }}
            >
              {showAdvancedOptions ? '▼' : '▶'} {t('advanced_options')}
            </button>

            {showAdvancedOptions && (
              <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    {t('custom_slug_label')}
                  </label>
                  <input
                    type="text"
                    value={customSlug}
                    onChange={(e) => setCustomSlug(e.target.value)}
                    placeholder={t('custom_slug_placeholder')}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    {t('max_uses_field')}
                  </label>
                  <input
                    type="number"
                    value={maxUses}
                    onChange={(e) => setMaxUses(e.target.value)}
                    placeholder={t('unlimited_placeholder')}
                    min="1"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    {t('expiry_days_label')}
                  </label>
                  <input
                    type="number"
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(e.target.value)}
                    placeholder={t('never_expires_placeholder')}
                    min="1"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
            )}

            {isPrivateContent && (
              <div style={{
                padding: '10px',
                backgroundColor: '#FEE2E2',
                borderRadius: '6px',
                marginBottom: '12px',
                fontSize: '13px',
                color: '#991B1B',
                textAlign: 'center'
              }}>
                🔒 {t('private_content_warning')}
              </div>
            )}

            {!signedIn && requiresLogin && !isPrivateContent && (
              <div style={{
                padding: '10px',
                backgroundColor: '#FEF3C7',
                borderRadius: '6px',
                marginBottom: '12px',
                fontSize: '13px',
                color: '#92400E',
                textAlign: 'center'
              }}>
                ⚠️ {t('login_required_warning')}
              </div>
            )}

            {inlineError && (
              <p style={{ color: '#dc2626', fontSize: '13px', margin: '0 0 10px 0' }}>{inlineError}</p>
            )}

            <button
              onClick={handleGenerateLink}
              disabled={isGenerating || isPrivateContent || (requiresLogin && !signedIn)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: (isGenerating || isPrivateContent || (requiresLogin && !signedIn)) ? '#ccc' : primaryColor,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: (isGenerating || isPrivateContent || (requiresLogin && !signedIn)) ? 'not-allowed' : 'pointer',
                transition: 'opacity 0.2s'
              }}
              onMouseOver={(e) => !isGenerating && !isPrivateContent && (!requiresLogin || signedIn) && (e.currentTarget.style.opacity = '0.9')}
              onMouseOut={(e) => !isGenerating && !isPrivateContent && (!requiresLogin || signedIn) && (e.currentTarget.style.opacity = '1')}
            >
              {isGenerating ? t('generating_button') : t('generate_button')}
            </button>
          </div>
        </div>

    </Modal>
  );
};

export default ShareLinkModal;
